import { useMemo } from "react";
import { Control, UseFormSetValue } from "react-hook-form";

import { InformationIcon } from "~/common/components";
import SignUpFormLayout from "~/common/components/signUp/SignUpFormLayout";
import SignUpProgress from "~/common/components/signUp/SignUpProgress";
import Select from "~/common/components/ui/select/Select";
import {
  generateDayOptions,
  generateYearOptions,
  monthsOptions,
} from "~/common/constants";
import {
  SignUpPatientSchemaType,
  UserBirthdateSchemaType,
} from "~/patients/utils";
import { FormErrors } from "~/patients/utils/types/forms";

interface NewPatientBirthdateFormProps {
  handleGoBack: () => void;
  totalSteps: number;
  setValue: UseFormSetValue<SignUpPatientSchemaType>;
  control?: Control<SignUpPatientSchemaType>;
  dateOfBirth: UserBirthdateSchemaType;
  onNext: () => Promise<void>;
  step: number;
  errors?: FormErrors<UserBirthdateSchemaType>;
}

export const NewPatientBirthdateForm = ({
  onNext,
  handleGoBack,
  totalSteps,
  setValue,
  control,
  dateOfBirth,
  errors,
  step,
}: NewPatientBirthdateFormProps) => {
  const { month, day, year } = { ...dateOfBirth };
  const handleNext = async () => {
    setValue("date_of_birth.birthday", `${year}-${month}-${day}`);
    await onNext();
  };

  const dayOptions = generateDayOptions(month, Number(year));

  const yearOptions = useMemo(() => generateYearOptions(), []);

  return (
    <>
      <SignUpProgress step={step} totalSteps={totalSteps} />
      <SignUpFormLayout
        title="Your date of birth"
        handleData={handleNext}
        submitLabel="Next"
        handleGoBack={handleGoBack}
      >
        <div className="flex size-full flex-grow flex-col justify-between lg:justify-normal lg:gap-12">
          <div className="flex flex-col gap-4">
            <Select
              error={errors?.month?.message}
              label="Month"
              placeholder={"Select month"}
              id="date_of_birth.month"
              options={monthsOptions}
              control={control}
              buttonClassName="py-3"
              containerClassName="gap-2"
            />
            <Select
              isDisabled={!month}
              error={errors?.day?.message}
              label="Day"
              placeholder="Select day"
              id="date_of_birth.day"
              options={dayOptions}
              control={control}
              buttonClassName="py-3"
              containerClassName="gap-2"
            />

            <Select
              error={errors?.year?.message}
              label="Year"
              placeholder="Select year"
              id="date_of_birth.year"
              options={yearOptions}
              control={control}
              buttonClassName="py-3"
              containerClassName="gap-2"
            />
          </div>
          {Boolean(errors?.birthday?.message) && (
            <div className={"flex gap-2 rounded-md bg-primary-50 p-2"}>
              <InformationIcon className="size-6 text-gray-400" />
              <p className="text-wrap text-base font-medium text-gray-600">
                {errors?.birthday?.message}
              </p>
            </div>
          )}
        </div>
      </SignUpFormLayout>
    </>
  );
};
